import { CheckCircleIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Step,
  StepIndicator,
  Stepper,
  StepSeparator,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Text,
} from "@chakra-ui/react";
import { NavLink, useParams } from "react-router-dom";
import {
  useTicket,
  useTicketDocuments,
  useTicketLogs,
} from "../hooks/tickets.hooks";
import PageTitle from "../components/PageTitle";
import Chat from "../components/Chat";
import Document from "../components/Document";
import { ReactNode, useMemo } from "react";
import InlineUserAvatar from "../components/InlineUserAvatar";
import TicketStatusBadge from "../components/TicketStatusBadge";
import { format } from "date-fns";
import TicketPriorityBadge from "../components/TicketPriorityBadge";
import { TicketLog, TicketLogChangeStatusMeta } from "../types/ticket.types";
import i18n from "../i18n";
import PersistingTabIndex from "../components/PersistingTabIndex";
import CommandStatusBadge from "../components/CommandStatusBadge";
import { useConnectedUser } from "../hooks/user.hooks";
import { useCommandActivityLogsPaginated } from "../hooks/commands.hooks";
import { getLongExposedId } from "../services/command.service";

const CommandLastStatusChangeAuthor = ({ id }: { id: number | string | null }) => {
  const logs = useCommandActivityLogsPaginated({ id: id ?? undefined })(1, 1, undefined, {
      type: "CHANGE_STATUS,UPDATE,",
    },
    ["-createdAt"]
  );

  if (logs?.data?.length === 0) {
    return null;
  }
  return <div style={{ textTransform: 'capitalize' }}>{logs?.data?.[0]?.user?.firstName} {logs?.data?.[0]?.user?.lastName}</div>
}

function TicketingDetailLogs({ id }: { id: number | string }) {
  const logs = useTicketLogs(id);

  const typeToTitle = new Map<string, (log: TicketLog) => string | ReactNode>([
    ["CREATED", () => "Création"],
    [
      "CHANGE_STATUS",
      (log: TicketLog) => {
        const meta = log.meta as TicketLogChangeStatusMeta;
        return <Text>{i18n.t(`ticket.status.${meta.to}`)}</Text>;
      },
    ],
  ]);
  const getTitle = (log: TicketLog) => {
    const title = typeToTitle.get(log.type);
    if (title) {
      return title(log);
    }
    return "";
  };

  const getDate = (log: TicketLog) => {
    return format(log.createdAt, "dd MMMM yyyy HH:mm");
  };

  const getDescription = (log: TicketLog) => {
    if (log.type === "CHANGE_STATUS") {
      const meta = log.meta as TicketLogChangeStatusMeta;
      const discount = meta?.discount ?? undefined;
      if (discount) {
        return (
          <Flex direction="row" gap={2} alignItems="center">
            <CheckCircleIcon color="#15803D" />
            <Text
              color="#15803D"
              lineHeight="21px"
              fontSize="15px"
              fontWeight="400"
            >
              Remise de {discount}% sur la commande
            </Text>
          </Flex>
        );
      }
    }
    return <></>;
  };

  if (!logs || logs.length === 0) {
    return <></>;
  }
  return (
    <Stepper
      index={logs.length}
      orientation="vertical"
      maxHeight="50vh"
      gap="0"
      colorScheme="brand"
      overflow="auto"
      size={"xs"}
    >
      {logs.map((log, index) => (
        <Step key={index}>
          <StepIndicator></StepIndicator>
          <Box w="360px" h="60px">
            <Flex justifyContent="space-between">
              <Text
                lineHeight="21px"
                fontSize="14px"
                fontWeight="600"
                color="black"
              >
                {getTitle(log)}
              </Text>
              <Text
                lineHeight="20px"
                fontSize="14px"
                fontWeight="400"
                color="gray.600"
              >
                {getDate(log)}
              </Text>
            </Flex>
            <Flex justifyContent="space-between">{getDescription(log)}</Flex>
          </Box>
          <StepSeparator />
        </Step>
      ))}
    </Stepper>
  );
}

function TicketingDetailDocuments({ id }: { id: number | string }) {
  const documents = useTicketDocuments(id);

  return (
    <Flex direction="column" gap={3}>
      {Array.isArray(documents) &&
        documents.map((document) => <Document file={document}></Document>)}
    </Flex>
  );
}

function TicketingDetailPage() {
  const user = useConnectedUser();
  const { id } = useParams();
  const ticket = useTicket(id);
  const defaultRoom = useMemo(() => `ticket_${id}`, [id]);
  const parsedId = useMemo(() => (id ? Number.parseInt(id) : null), [id]);
  const commandId = useMemo(() => {
    if (ticket?.command?.exposedId) {
      return getLongExposedId(ticket?.command);
    }
    return ``;
  }, [ticket?.command]);
  if (!ticket) {
    return <>{"Pas d'information sur ce ticket."}</>;
  }
  return (
    <Box h={"90vh"}>
      <Breadcrumb
        spacing="8px"
        separator={<ChevronRightIcon color="gray.500" />}
      >
        <BreadcrumbItem>
          <BreadcrumbLink
            href="/tickets"
            color="gray.600"
            fontWeight={600}
            fontSize="14px"
          >
            Tickets SAV / Assistance
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <BreadcrumbLink
            href="#"
            color="brand.700"
            fontWeight={600}
            fontSize="14px"
          >
            {format(ticket.createdAt, "yyyy")}-{id}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Box w={"400px"}>
        <Flex alignItems="center" gap={4} py={8}>
          <PageTitle display={"inline"}>
            {format(ticket.createdAt, "yyyy")}-{id}
          </PageTitle>
          <TicketStatusBadge
            id={ticket.id}
            editable={false}
          ></TicketStatusBadge>
        </Flex>

        <Box>
          <Flex
            justifyContent="space-between"
            borderBottom="solid 1px"
            borderColor="gray.100"
            py={2}
          >
            <Text lineHeight="40px" fontSize="16px" fontWeight="600">
              Motif
            </Text>
            <Text lineHeight="40px" fontSize="16px">
              {ticket.ticketLabel.label}
            </Text>
          </Flex>
          <Flex
            justifyContent="space-between"
            borderBottom="solid 1px"
            borderColor="gray.100"
            py={2}
          >
            <Text lineHeight="40px" fontSize="16px" fontWeight="600">
              Lié à
            </Text>
            <Flex lineHeight="40px" fontSize="16px" gap={4}>
              <NavLink to={`/orders/${ticket.commandId}`}>
                <Button variant="link" color="brand.700">
                  {commandId}
                </Button>
              </NavLink>
            </Flex>
          </Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            borderBottom="solid 1px"
            borderColor="gray.100"
            py={2}
          >
            <Text lineHeight="40px" fontSize="16px" fontWeight="600">
              Priorité
            </Text>
            <TicketPriorityBadge
              priority={ticket.priority}
            ></TicketPriorityBadge>
          </Flex>
          {user?.isSuperAdmin && (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              borderBottom="solid 1px"
              borderColor="gray.100"
              py={2}
            >
              <Text lineHeight="40px" fontSize="16px" fontWeight="600">
                Statut commande
              </Text>
              <CommandStatusBadge
                id={ticket.commandId ?? null}
                editable={true}
              ></CommandStatusBadge>
            </Flex>
          )}
          {user?.isSuperAdmin && (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              borderBottom="solid 1px"
              borderColor="gray.100"
              py={2}
            >
              <Text lineHeight="40px" fontSize="16px" fontWeight="600">
                Statut modifié par
              </Text>
              <CommandLastStatusChangeAuthor id={ticket.commandId ?? null} />
            </Flex>
          )}
          <Flex
            justifyContent="space-between"
            borderBottom="solid 1px"
            borderColor="gray.100"
            py={2}
          >
            <Text lineHeight="40px" fontSize="16px" fontWeight="600">
              Ouvert par
            </Text>
            {ticket.openById && (
              <InlineUserAvatar id={ticket.openById}></InlineUserAvatar>
            )}
            {!ticket.openById && <Text>...</Text>}
          </Flex>
        </Box>

        <Box py={10}></Box>

        <PersistingTabIndex id="tickets">
          <TabList>
            <Tab
              _selected={{ color: "brand.700", borderColor: "brand.700" }}
              w="50%"
            >
              Activité
            </Tab>
            <Tab
              _selected={{ color: "brand.700", borderColor: "brand.700" }}
              w="50%"
            >
              Documents
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              {id && <TicketingDetailLogs id={id}></TicketingDetailLogs>}
            </TabPanel>
            <TabPanel px={0}>
              {id && (
                <TicketingDetailDocuments id={id}></TicketingDetailDocuments>
              )}
            </TabPanel>
          </TabPanels>
        </PersistingTabIndex>
      </Box>

      <Chat
        position={"absolute"}
        top={0}
        right={0}
        w={"calc(100vw - 280px - 480px)"}
        defaultRoom={defaultRoom}
        ticketId={parsedId}
      ></Chat>
    </Box>
  );
}

export default TicketingDetailPage;
